import { Popover } from "@headlessui/react";
import { range } from "lodash";
import { useMemo, useState } from "react";
import { usePopper } from "react-popper";
import {
  ComplianceProgramReportAnswerFragment,
  ComplianceProgramReportAssessmentFragment,
  ComplianceProgramReportPrevAnswerFragment,
  GetComplianceProgramForReportQuery,
} from "types/graphql-schema";

import PrevAnswerArrow from "@apps/assessments/components/prev-answer-arrow";
import { TableBodyCell } from "@components/table/table";
import { assertNonNull } from "@helpers/helpers";

import { isAssessmentReportAnswerType } from "../helpers";
import AssessmentAnswerCellPopover from "./assessment-answer-cell-popover";
import { PerformanceAssessmentTableQuestion } from "./performance-assessment-table";
import PerformanceAssessmentTableEmptyCell from "./performance-assessment-table-empty-cell";

const PerformanceAssessmentTableRowAnswerCell = ({
  complianceProgram,
  assessmentTemplate,
  assessment,
  answers,
  programHasCalibration,
  prevAnswers,
  question,
  canCalibrate,
  canSuggestCalibration,
  showPrevAnswer,
}: {
  complianceProgram: NonNullable<
    GetComplianceProgramForReportQuery["complianceProgram"]
  >;
  assessmentTemplate: NonNullable<
    NonNullable<
      GetComplianceProgramForReportQuery["complianceProgram"]
    >["performanceAssessmentTemplate"]
  >;
  assessment: ComplianceProgramReportAssessmentFragment;
  answers: ComplianceProgramReportAnswerFragment[];
  programHasCalibration: boolean;
  prevAnswers: ComplianceProgramReportPrevAnswerFragment[];
  question: PerformanceAssessmentTableQuestion;
  canCalibrate: boolean;
  canSuggestCalibration: boolean;
  showPrevAnswer: boolean;
}) => {
  const answer = useMemo(
    () => answers.find((a) => a.questionId === question.id),
    [answers, question]
  );
  const prevAnswer = useMemo(
    () => prevAnswers.find((a) => a.questionId === question.id),
    [prevAnswers, question]
  );
  const showHasCalibrations = useMemo(() => {
    const hasCalibrations =
      answer?.__typename === "RangeAssessmentAnswerNode" ||
      answer?.__typename === "TalentIndicatorAssessmentAnswerNode"
        ? answer.hasCalibrations
        : false;
    return canSuggestCalibration && hasCalibrations;
  }, [answer, canSuggestCalibration]);

  const [popperRefElement, setPopperRefElement] =
    useState<HTMLButtonElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null
  );
  const { styles, attributes } = usePopper(popperRefElement, popperElement, {
    placement: "left-end",
  });

  const questionLabels = useMemo(
    () =>
      range(question.startValue, question.endValue + 1).reduce(
        (labelObj, value, index) => ({
          ...labelObj,
          [value]: question.labels[index] || value.toString(),
        }),
        {} as Record<string, string>
      ),
    [question.endValue, question.labels, question.startValue]
  );

  if (!answer || !isAssessmentReportAnswerType(answer)) {
    return <PerformanceAssessmentTableEmptyCell padLeft={showPrevAnswer} />;
  }

  const integerAnswer = assertNonNull(answer.integerAnswer);
  const prevAnswerArrow = showPrevAnswer && (
    <div className="w-4">
      <PrevAnswerArrow
        currentIntegerAnswer={integerAnswer}
        prevAnswer={prevAnswer}
        question={question}
      />
    </div>
  );
  const calibrationsStar = showHasCalibrations && (
    <div className="font-semibold text-amber-700">*</div>
  );
  return (
    <TableBodyCell
      className="p-0 h-full"
      key={question.id}
      aria-label="Performance assessment report answer cell"
    >
      <Popover className="h-full">
        {({ open }) => (
          <>
            <Popover.Button
              as="div"
              ref={setPopperRefElement}
              className="flex items-center hover:cursor-pointer hover:text-gray-700 hover:bg-black/5 px-5 py-3 w-full h-full"
              role="button"
            >
              <div className="flex items-center gap-2">
                {prevAnswerArrow}

                <div
                  className="max-w-36 text-ellipsis overflow-hidden text-nowrap"
                  title={`${questionLabels[integerAnswer]}`}
                >
                  {questionLabels[integerAnswer]}
                </div>

                {calibrationsStar}
              </div>
            </Popover.Button>
            {open && (
              <AssessmentAnswerCellPopover
                complianceProgram={complianceProgram}
                assessmentTemplate={assessmentTemplate}
                answer={answer}
                canCalibrate={canCalibrate}
                canSuggestCalibration={canSuggestCalibration}
                programHasCalibration={programHasCalibration}
                questionLabels={questionLabels}
                assessment={assessment}
                attributes={attributes.popper ?? {}}
                calibrationsStar={calibrationsStar}
                prevAnswerArrow={prevAnswerArrow}
                question={question}
                responder={assertNonNull(assessment.responder)}
                styles={styles.popper}
                setElement={setPopperElement}
              />
            )}
          </>
        )}
      </Popover>
    </TableBodyCell>
  );
};

export default PerformanceAssessmentTableRowAnswerCell;
